import { Alert, Button, Chip, FormControl, Grid, IconButton, MenuItem, Select, Snackbar, Tooltip } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../orders/Orders.scss';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { Cancel, Check, ContentCopy } from '@mui/icons-material';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import { Link, useSearchParams } from 'react-router-dom';
import TimeUtils from '../utils/TimeUtils';
import { AccountData, ExternalVoucherService, ExternalVoucherStatus, OrderData } from '../api';
import { WorkshopCircle } from '../common/ui/WorkshopCircle';
import { useLanguage } from '../common/GeneralUtils';
import { CalendarView, DATE_FORMAT } from '../calendar/calendarWrapper/CalendarWrapper';
import { CellProps, useTable } from '../table/useTable';
import { useAuth } from '../session/InternalAuthProvider';
import { getDateFormat, getTimeFormat } from '../common/getDateAndTimeFormat';
import { TABLE_PHONE_ID, TABLE_PRICE_ID } from '../table/TableComponent';
import EditOrderDialog from '../calendar/dialogs/EditOrderDialog/EditOrderDialog';
import { useCalendar } from '../calendar/CalendarContext/CalendarContext';
import ConfirmationDialog from '../common/ui/ConfimationDialog';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
import { ALL_EXTERNAL_VOUCHER_STATUSES, getExternalVoucherStatusChip } from './utils';

type ExternalVoucherFilterFetch = {
  statuses?: ExternalVoucherStatus[];
};

export const externalVoucherFetcher = async ({
  search,
  page,
  perPage,
  filterState,
}: {
  search?: string;
  page: number;
  perPage: number;
  filterState?: ExternalVoucherFilterFetch;
}) => ExternalVoucherService.getExternalVoucherRedemptions(page, perPage, search, filterState?.statuses);

export const ExternalVouchersTable = React.memo(() => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { isAtLeastSupervisor } = UseAuthPermissions();
  const { editOrderDrawerState, closeEditOrderDrawer, openEditOrderDrawer } = useCalendar();
  const { shortDateFormat } = getDateFormat(account);
  const timeFormat = getTimeFormat(account);
  const language = useLanguage();
  const [searchParams] = useSearchParams();

  const [verifyIsOpen, setVerifyIsOpen] = useState(false);
  const [rejectIsOpen, setRejectIsOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderData | undefined>(undefined);
  const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);
  const [copiedIsOpen, setCopiedIsOpen] = useState(false);

  // The calendar badge and the orders-table chips deep-link here narrowed to one status.
  const getInitialStatuses = () => {
    const statusParam = searchParams.get('status') as ExternalVoucherStatus | null;
    if (statusParam && ALL_EXTERNAL_VOUCHER_STATUSES.includes(statusParam)) {
      return [statusParam];
    }
    return ALL_EXTERNAL_VOUCHER_STATUSES;
  };

  const [selectedStatuses, setSelectedStatuses] = useState<Array<ExternalVoucherStatus>>(getInitialStatuses);
  // Kept stable so it can key the SWR cache without re-triggering useTable's debounce every render.
  const filterState = useMemo(() => ({ statuses: selectedStatuses }), [selectedStatuses]);

  function formatRedeemedAt(created_at: string) {
    const createdAt = TimeUtils.getLocalTime(created_at, account.timezone);
    const isToday = createdAt.isSame(moment(), 'day');
    return (
      <Typography
        variant={isToday ? 'body1' : 'body2'}
        color={isToday ? 'primary.main' : undefined}
        fontWeight={isToday ? 700 : undefined}>
        {createdAt.format(isToday ? timeFormat : shortDateFormat)}
      </Typography>
    );
  }

  const tableDetails: CellProps<OrderData>[] = [
    {
      id: 'redeemedAt',
      alignment: 'center',
      label: t('externalVouchers.redeemedAt'),
      render: (order: OrderData) =>
        order.external_voucher_redemption ? formatRedeemedAt(order.external_voucher_redemption.created_at) : '-',
    },
    {
      id: 'name',
      alignment: 'center',
      label: t('orders.name'),
      render: (order: OrderData) => `${order.firstname} ${order.lastname}`,
    },
    {
      id: TABLE_PHONE_ID,
      alignment: 'center',
      label: t('orders.phone'),
    },
    {
      id: 'email',
      alignment: 'center',
      label: t('orders.email'),
      render: (order: OrderData) => (
        <Typography variant="body2" sx={{ textTransform: 'lowercase' }}>
          {order.email}
        </Typography>
      ),
    },
    {
      id: 'workshop',
      alignment: 'center',
      label: t('orders.workshop'),
      render: (order: OrderData) => (
        <WorkshopCircle account={account} orderPageId={order.order_page_id} language={language} withTooltip />
      ),
    },
    {
      id: 'orderDate',
      alignment: 'center',
      label: t('order_date'),
      render: (order: OrderData) => {
        if (!order.date) return '-';
        const momentDate = moment(order.date);
        const calendarView = localStorage.getItem('webook-calendarview');
        const weekStart = momentDate
          .clone()
          .startOf(calendarView === CalendarView.THREE_DAY ? 'day' : (calendarView as any));
        return (
          <Link to={`/dashboard/calendar/${weekStart.format(DATE_FORMAT)}?entityid=${order.parent_id || order.id}`}>
            {momentDate.format(shortDateFormat)}
          </Link>
        );
      },
    },
    {
      id: 'order_number',
      alignment: 'center',
      label: t('order_number'),
      render: (order: OrderData) => (order.order_number ? `#${order.order_number}` : '-'),
    },
    {
      id: TABLE_PRICE_ID,
      alignment: 'center',
      label: t('externalVouchers.price'),
    },
    {
      id: 'code',
      alignment: 'center',
      width: 220,
      label: t('externalVouchers.code'),
      render: (order: OrderData) => {
        const code = order.external_voucher_redemption?.code;
        if (!code) return '-';
        return (
          <Grid container alignItems="center" gap={0.5} flexWrap="nowrap">
            <Tooltip title={code} arrow>
              <Typography
                variant="body2"
                fontFamily="monospace"
                sx={{
                  textTransform: 'lowercase',
                  maxWidth: 170,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                {code}
              </Typography>
            </Tooltip>
            <Tooltip title={t('copyToClipboard.copy')} arrow>
              <IconButton
                size="small"
                onClick={async () => {
                  await navigator.clipboard.writeText(code);
                  setCopiedIsOpen(true);
                }}>
                <ContentCopy sx={{ width: 16 }} />
              </IconButton>
            </Tooltip>
          </Grid>
        );
      },
    },
    {
      id: 'redemptionStatus',
      alignment: 'center',
      label: t('externalVouchers.statusCol'),
      render: (order: OrderData) => getExternalVoucherStatusChip(order.external_voucher_redemption?.status, t),
    },
    {
      // The redemption record and the order status are separate sources of truth and can drift,
      // so both are shown side by side.
      id: 'orderStatus',
      alignment: 'center',
      label: t('externalVouchers.orderStatusCol'),
      render: (order: OrderData) => (
        <Typography variant="body2">{t(`order_status.${order.status.toLowerCase()}`)}</Typography>
      ),
    },
    {
      id: 'orderlink',
      alignment: 'center',
      label: t('vouchers.orderlink'),
      render: (order: OrderData) => (
        <Grid sx={{ cursor: 'pointer' }} item onClick={() => openEditOrderDrawer({ order })}>
          <Tooltip title={t('info')} arrow>
            <ConfirmationNumber sx={{ color: 'text.secondary' }} />
          </Tooltip>
        </Grid>
      ),
    },
    {
      id: 'actions',
      alignment: 'center',
      width: 190,
      label: t('externalVouchers.actions'),
      render: (order: OrderData) => {
        const isPending = order.external_voucher_redemption?.status === ExternalVoucherStatus.PENDING_VERIFICATION;
        const canReview = isPending && isAtLeastSupervisor;
        const disabledReason = t('noPermissions');

        return (
          isPending && (
            <Grid container justifyContent="flex-end" flexWrap="nowrap" gap={1}>
              <Tooltip title={canReview ? t('externalVouchers.verify.tooltip') : disabledReason} arrow>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={!canReview}
                    onClick={() => {
                      setSelectedOrder(order);
                      setVerifyIsOpen(true);
                    }}
                    sx={{ minHeight: 32, whiteSpace: 'nowrap' }}>
                    {t('externalVouchers.verify.button')}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={canReview ? t('externalVouchers.reject.tooltip') : disabledReason} arrow>
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    disabled={!canReview}
                    onClick={() => {
                      setSelectedOrder(order);
                      setRejectIsOpen(true);
                    }}
                    sx={{ minHeight: 32, whiteSpace: 'nowrap' }}>
                    {t('externalVouchers.reject.button')}
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          )
        );
      },
    },
  ];

  const { TableComponent, data, mutate } = useTable<OrderData>({
    opId: 'getExternalVoucherRedemptions',
    fetcher: externalVoucherFetcher,
    tableDetails,
    filterState,
    fillHeight: true,
    actionsComponent: (
      <Grid container gap={1}>
        <FormControl>
          <Grid container alignItems="center" gap={1}>
            <Typography>{t('externalVouchers.statusLabel')}</Typography>
            <Select
              sx={{ minWidth: { xs: 200, sm: 300 } }}
              size="small"
              multiple
              value={selectedStatuses}
              renderValue={(selected) => (
                <Grid container gap={1}>
                  {selected.map((status) => (
                    <Chip
                      key={status}
                      label={t(`externalVouchers.status.${status}`)}
                      onDelete={() => {
                        const remainingStatuses = selected.filter((s) => s !== status);
                        setSelectedStatuses(
                          remainingStatuses.length === 0 ? ALL_EXTERNAL_VOUCHER_STATUSES : remainingStatuses,
                        );
                      }}
                      deleteIcon={<Cancel onMouseDown={(event) => event.stopPropagation()} />}
                    />
                  ))}
                </Grid>
              )}
              onChange={(e) => {
                const value = e.target.value as Array<ExternalVoucherStatus>;
                setSelectedStatuses(value.length === 0 ? ALL_EXTERNAL_VOUCHER_STATUSES : value);
              }}>
              {ALL_EXTERNAL_VOUCHER_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  <Grid container alignItems="center">
                    <Grid item xs>
                      {t(`externalVouchers.status.${status}`)}
                    </Grid>
                    {selectedStatuses.includes(status) && (
                      <Grid item>
                        <Check />
                      </Grid>
                    )}
                  </Grid>
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </FormControl>
      </Grid>
    ),
  });

  // Both endpoints return the updated order, so the row is replaced in place rather than refetched.
  const replaceRow = async (updated: OrderData) => {
    await mutate(data.map((order) => (order.id === updated.id ? updated : order)));
  };

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0 }}>
      {TableComponent}
      <EditOrderDialog
        editOrderDialogState={editOrderDrawerState}
        closeEditOrderDialog={() => {
          void mutate();
          void closeEditOrderDrawer();
        }}
      />
      <ConfirmationDialog
        title={t('externalVouchers.verify.title')}
        ctaText={t('externalVouchers.verify.cta')}
        confirmButtonColor="primary"
        content={<Typography>{t('externalVouchers.verify.description')}</Typography>}
        isOpen={verifyIsOpen}
        onClose={async (actionConfirmed) => {
          if (actionConfirmed && selectedOrder) {
            const updated = await ExternalVoucherService.verifyExternalVoucher(selectedOrder.id);
            await replaceRow(updated);
            setSuccessMessage(t('externalVouchers.verify.success'));
          }
          setVerifyIsOpen(false);
        }}
      />
      <ConfirmationDialog
        title={t('externalVouchers.reject.title')}
        ctaText={t('externalVouchers.reject.cta')}
        confirmButtonColor="error"
        content={<Typography>{t('externalVouchers.reject.description')}</Typography>}
        isOpen={rejectIsOpen}
        onClose={async (actionConfirmed) => {
          if (actionConfirmed && selectedOrder) {
            const updated = await ExternalVoucherService.rejectExternalVoucher(selectedOrder.id);
            await replaceRow(updated);
            setSuccessMessage(t('externalVouchers.reject.success'));
          }
          setRejectIsOpen(false);
        }}
      />
      <Snackbar
        open={!!successMessage}
        onClose={() => setSuccessMessage(undefined)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSuccessMessage(undefined)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={copiedIsOpen}
        onClose={() => setCopiedIsOpen(false)}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setCopiedIsOpen(false)} severity="success">
          {t('copiedToClipboard')}
        </Alert>
      </Snackbar>
    </Grid>
  );
});
